// KEEPING AN OPEN TAB ON THE BUILD THAT IS LIVE.
//
// Why this exists (2026-09-25). The Daily Tasks / Follow-Up recut went live at
// 15:05Z on 2026-09-23. The next morning the rep ran both operations and
// reported, point by point, exactly what the OLD engine did — Daily Tasks
// listing companies with no task at all, companies whose only task was
// future-dated, companies he had written off as "not a fit". The ledger showed
// why: his runs logged "Daily Tasks: built — 23 selected", which only the
// pre-recut bundle can say. His tab had been open since before the deploy, and
// a single-page app never reloads itself: index.html is served no-cache and
// there is no service worker, so ANY fresh load gets the new build — but a tab
// left open keeps running whatever it loaded, for days, and every deploy
// silently skips the one person the change was for.
//
// So an open tab now compares the bundle it is running (the hashed
// main.<hash>.js named by its own <script>) with the one the live index.html
// names, and:
//   - reloads itself when that costs the reader nothing — the tab is in the
//     background, or they have just come back to it — unless work is in
//     flight (a run, a streaming answer, text typed into a field);
//   - otherwise shows a one-line "new version" banner (Shell.jsx);
//   - and, hardest rule, never STARTS an operation on a stale build: the
//     click is remembered in this tab's sessionStorage, the page reloads onto
//     the live build, and the operation starts there (Chat.jsx).
// A dev build (unhashed main.js) and a failed check both read as "current":
// an unknown is never a reason to reload anyone's page.

const BUNDLE_RE = /\bmain\.([0-9a-f]{8,})\.js\b/

const RELOADED_KEY = 'revailosales.reloadedFor'
const PENDING_KEY = 'revailosales.pendingOperation'

// How long a remembered click survives the reload it caused. A reload takes a
// second or two; anything older is a click from some other visit.
export const PENDING_MAX_AGE_MS = 2 * 60 * 1000

/** The content hash in a bundle name ('main.ad17d9ea64d8c908b236.js'), or ''. */
export function bundleHashOf(text) {
  const m = String(text || '').match(BUNDLE_RE)
  return m ? m[1] : ''
}

/** The hash of the bundle THIS page is running, from its own script tag. */
export function runningBundleHash(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || typeof doc.querySelectorAll !== 'function') return ''
  for (const script of doc.querySelectorAll('script[src]')) {
    const hash = bundleHashOf(script.getAttribute('src'))
    if (hash) return hash
  }
  return ''
}

/** The running bundle's file name, for the ledger ('' on a dev build). */
export function runningBundleName(doc) {
  const hash = runningBundleHash(doc)
  return hash ? `main.${hash}.js` : ''
}

/**
 * The hash the LIVE index.html names, fetched past every cache. '' on any
 * failure — offline, a timeout, an HTML page with no hashed bundle — which
 * the verdict below reads as "current", never as "stale".
 */
export async function latestBundleHash({ fetchImpl = globalThis.fetch, timeoutMs = 4000, now = Date.now() } = {}) {
  if (typeof fetchImpl !== 'function') return ''
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null
  try {
    const res = await fetchImpl(`/index.html?v=${now}`, {
      cache: 'no-store',
      credentials: 'omit',
      ...(controller ? { signal: controller.signal } : {}),
    })
    if (!res || !res.ok) return ''
    return bundleHashOf(await res.text())
  } catch {
    return ''
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * 'current' | 'stale' | 'stuck'. PURE.
 *   current — same build, or either side unknown (never act on an unknown)
 *   stale   — the live index names a different bundle than this page runs
 *   stuck   — stale, but this tab ALREADY reloaded for exactly that build and
 *             is still on the old one (an edge still serving the old index):
 *             reloading again would loop, so the caller carries on and says so
 */
export function updateVerdict({ running = '', latest = '', reloadedFor = '' } = {}) {
  if (!running || !latest || running === latest) return 'current'
  return reloadedFor === latest ? 'stuck' : 'stale'
}

/**
 * What an open tab does about a verdict. PURE. 'reload' only when it costs the
 * reader nothing: the tab is in the background, or they have just come back
 * to it — and never over work in flight. Everything else is a banner.
 */
export function updateAction({ verdict = 'current', hidden = false, returning = false, busy = false } = {}) {
  if (verdict === 'current') return 'none'
  if (verdict === 'stale' && !busy && (hidden || returning)) return 'reload'
  return 'banner'
}

function session() {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}

export function readReloadedFor() {
  try {
    return String(session()?.getItem(RELOADED_KEY) || '')
  } catch {
    return ''
  }
}

/** Reload onto the live build, remembering which build it was for (loop guard). */
export function reloadForUpdate(latest, win = typeof window !== 'undefined' ? window : null) {
  try {
    session()?.setItem(RELOADED_KEY, String(latest || ''))
  } catch {
    /* private mode: the loop guard is lost, the reload still happens */
  }
  win?.location?.reload()
}

/** Remember an operation click across the reload it is about to cause. */
export function stashPendingOperation({ operationId, scopeCode = '' } = {}, now = Date.now()) {
  if (!operationId) return false
  try {
    session()?.setItem(PENDING_KEY, JSON.stringify({ operationId: String(operationId), scopeCode: String(scopeCode || ''), at: now }))
    return true
  } catch {
    return false
  }
}

/** The remembered click, once, if it is fresh — and it is forgotten either way. */
export function takePendingOperation({ now = Date.now(), maxAgeMs = PENDING_MAX_AGE_MS } = {}) {
  const store = session()
  if (!store) return null
  let raw = null
  try {
    raw = store.getItem(PENDING_KEY)
    store.removeItem(PENDING_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const v = JSON.parse(raw)
    if (!v || !v.operationId || !(now - Number(v.at) >= 0 && now - Number(v.at) <= maxAgeMs)) return null
    return { operationId: String(v.operationId), scopeCode: String(v.scopeCode || '') }
  } catch {
    return null
  }
}

/**
 * Text typed into a field on the page — a composer draft, a note — is work a
 * reload would throw away, so it holds the reload back (the banner still
 * shows). Read-only, disabled and invisible fields do not count.
 */
export function hasUnsavedText(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || typeof doc.querySelectorAll !== 'function') return false
  const fields = doc.querySelectorAll(
    'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="url"], input[type="tel"]',
  )
  for (const field of fields) {
    if (field.readOnly || field.disabled) continue
    if (!String(field.value || '').trim()) continue
    if (field.offsetParent === null && field.getClientRects?.().length === 0) continue
    return true
  }
  return false
}

/**
 * Before an operation starts: is this tab on the live build? Resolves to
 * { ok: true } to go ahead (current, unknown, or stuck), or { ok: false,
 * latest } when the caller must remember the click and reload instead.
 */
export async function checkBeforeRun({ fetchImpl, doc } = {}) {
  const running = runningBundleHash(doc)
  if (!running) return { ok: true, verdict: 'current' }
  const latest = await latestBundleHash({ fetchImpl })
  const verdict = updateVerdict({ running, latest, reloadedFor: readReloadedFor() })
  return verdict === 'stale' ? { ok: false, verdict, latest } : { ok: true, verdict }
}

/**
 * Watch for a new build while the tab is open: shortly after load, every ten
 * minutes, and whenever the tab is hidden or shown (at most once a minute).
 * `isBusy()` is the caller's "work in flight" test. Returns a stop function.
 */
export function startUpdateWatch({
  onStale = () => {},
  isBusy = () => false,
  intervalMs = 10 * 60 * 1000,
  firstCheckMs = 30 * 1000,
  minGapMs = 60 * 1000,
} = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return () => {}
  const running = runningBundleHash()
  if (!running) return () => {}
  let stopped = false
  let inflight = false
  let lastAt = 0
  async function check({ returning = false } = {}) {
    if (stopped || inflight) return
    const now = Date.now()
    if (now - lastAt < minGapMs) return
    lastAt = now
    inflight = true
    try {
      const latest = await latestBundleHash()
      if (stopped) return
      const verdict = updateVerdict({ running, latest, reloadedFor: readReloadedFor() })
      let busy = true
      try {
        busy = Boolean(isBusy()) || hasUnsavedText()
      } catch {
        busy = true
      }
      const action = updateAction({ verdict, hidden: document.visibilityState === 'hidden', returning, busy })
      if (action === 'reload') reloadForUpdate(latest)
      else if (action === 'banner') onStale({ running, latest, verdict })
    } finally {
      inflight = false
    }
  }
  const onVisibility = () => check({ returning: document.visibilityState === 'visible' })
  const onFocus = () => check({ returning: true })
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('focus', onFocus)
  const first = setTimeout(() => check(), firstCheckMs)
  const every = setInterval(() => check(), intervalMs)
  return () => {
    stopped = true
    clearTimeout(first)
    clearInterval(every)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('focus', onFocus)
  }
}
